import { useEffect, useState, useCallback, useMemo } from "react";
import { useApplicationForm } from "@/context/ApplicationFormContext";
import {
  DocumentType,
  DocumentFile,
  getRequiredDocuments,
  getOptionalDocuments,
  DOCUMENT_LABELS,
  DOCUMENT_DESCRIPTIONS,
} from "@/lib/application";
import { cn } from "@/lib/utils";

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
    </svg>
  );
}

function FileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
  );
}

interface DocumentUploadCardProps {
  docType: DocumentType;
  document: DocumentFile | undefined;
  onUpload: (file: File) => void;
  onRemove: () => void;
  isOptional?: boolean;
}

function DocumentUploadCard({ docType, document, onUpload, onRemove, isOptional }: DocumentUploadCardProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        onUpload(file);
      }
    },
    [onUpload]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
    }
  };

  return (
    <div className="bg-white rounded-2xl border-[3px] border-border/50 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
              document?.uploaded ? "bg-green-100" : "bg-muted"
            )}
          >
            {document?.uploaded ? (
              <CheckCircleIcon className="w-5 h-5 text-green-600" />
            ) : (
              <FileIcon className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-medium">
              {DOCUMENT_LABELS[docType]}
              {isOptional && (
                <span className="ml-2 text-[11px] font-normal text-muted-foreground">(необов'язково)</span>
              )}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
              {DOCUMENT_DESCRIPTIONS[docType]}
            </p>
          </div>
        </div>
      </div>

      {document?.uploaded ? (
        <div className="px-4 pb-4">
          <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
            <FileIcon className="w-5 h-5 text-green-600 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-medium text-green-700 truncate">{document.name}</p>
              <p className="text-[10px] text-green-600">Завантажено</p>
            </div>
            <button
              type="button"
              onClick={onRemove}
              className="p-2 rounded-lg hover:bg-green-100 transition-colors"
            >
              <TrashIcon className="w-4 h-4 text-green-600" />
            </button>
          </div>
        </div>
      ) : (
        <div className="px-4 pb-4">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={handleDrop}
            className={cn(
              "flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all",
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-border/50 hover:border-primary/50 hover:bg-muted/30"
            )}
          >
            <UploadIcon className={cn("w-8 h-8 mb-2", isDragOver ? "text-primary" : "text-muted-foreground")} />
            <p className="text-[12px] font-medium text-center">
              Перетягніть файл або <span className="text-primary">оберіть</span>
            </p>
            <p className="text-[10px] text-muted-foreground mt-1">PDF, JPG, PNG до 10 МБ</p>
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
        </div>
      )}
    </div>
  );
}

export function StepDocuments() {
  const { formData, updateDocuments } = useApplicationForm();
  const { recipient, building, documents } = formData;

  // Get required documents based on recipient type and building info
  const requiredDocs = useMemo(() => {
    return recipient.recipient_type
      ? getRequiredDocuments(
          recipient.recipient_type,
          building.boiler_room_type,
          building.has_tech_passport
        )
      : [];
  }, [recipient.recipient_type, building.boiler_room_type, building.has_tech_passport]);

  // Get optional documents
  const optionalDocs = useMemo(() => getOptionalDocuments(), []);

  // All documents (required + optional)
  const allDocs = useMemo(() => [...requiredDocs, ...optionalDocs], [requiredDocs, optionalDocs]);

  // Initialize documents if needed
  useEffect(() => {
    if (allDocs.length > 0 && documents.length === 0) {
      const initialDocs: DocumentFile[] = allDocs.map((type) => ({
        id: type,
        type,
        file: null,
        name: "",
        uploaded: false,
      }));
      updateDocuments(initialDocs);
    }
  }, [allDocs, documents.length, updateDocuments]);

  const handleUpload = (docType: DocumentType, file: File) => {
    const updated = documents.map((doc) =>
      doc.type === docType
        ? { ...doc, file, name: file.name, uploaded: true }
        : doc
    );

    // If document doesn't exist, add it
    if (!updated.find((d) => d.type === docType)) {
      updated.push({
        id: docType,
        type: docType,
        file,
        name: file.name,
        uploaded: true,
      });
    }

    updateDocuments(updated);
  };

  const handleRemove = (docType: DocumentType) => {
    const updated = documents.map((doc) =>
      doc.type === docType
        ? { ...doc, file: null, name: "", uploaded: false }
        : doc
    );
    updateDocuments(updated);
  };

  const uploadedCount = documents.filter((d) => d.uploaded).length;
  const totalCount = requiredDocs.length;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-[15px] font-semibold mb-2">Необхідні документи</h3>
        <p className="text-[12px] text-muted-foreground mb-4">
          Завантажте всі необхідні документи для подання заяви
        </p>

        {/* Progress */}
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl mb-6">
          <div className="flex-1">
            <div className="flex justify-between mb-1">
              <span className="text-[12px] font-medium">Завантажено документів</span>
              <span className="text-[12px] font-semibold">{uploadedCount} / {totalCount}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full transition-all rounded-full",
                  uploadedCount === totalCount ? "bg-green-500" : "bg-primary"
                )}
                style={{ width: `${totalCount > 0 ? (uploadedCount / totalCount) * 100 : 0}%` }}
              />
            </div>
          </div>
          {uploadedCount === totalCount && totalCount > 0 && (
            <CheckCircleIcon className="w-6 h-6 text-green-500 shrink-0" />
          )}
        </div>
      </div>

      {/* Required documents */}
      <div className="grid gap-4">
        {requiredDocs.map((docType) => (
          <DocumentUploadCard
            key={docType}
            docType={docType}
            document={documents.find((d) => d.type === docType)}
            onUpload={(file) => handleUpload(docType, file)}
            onRemove={() => handleRemove(docType)}
          />
        ))}
      </div>

      {/* Optional documents */}
      {optionalDocs.length > 0 && (
        <div className="mt-6">
          <h3 className="text-[15px] font-semibold mb-2">Додаткові документи</h3>
          <p className="text-[12px] text-muted-foreground mb-4">
            Ці документи не є обов'язковими, але можуть прискорити розгляд заявки
          </p>
          <div className="grid gap-4">
            {optionalDocs.map((docType) => (
              <DocumentUploadCard
                key={docType}
                docType={docType}
                document={documents.find((d) => d.type === docType)}
                onUpload={(file) => handleUpload(docType, file)}
                onRemove={() => handleRemove(docType)}
                isOptional
              />
            ))}
          </div>
        </div>
      )}

      {/* Help text */}
      <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-4">
        <p className="text-[12px] text-amber-700">
          <strong>Важливо:</strong> Всі документи повинні бути чіткими та читабельними. 
          Витяг з ЄДР має бути сформований датою подання заяви.
        </p>
      </div>
    </div>
  );
}
